// IMPORTANT: Import Sentry instrumentation first, before any other imports
import './instrument';
import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import { setupGeoComplyConsoleFilter } from './lib/console-filter'
import { initPixel } from './lib/pixel'

// Filter out expected GeoComply SDK console errors
// These are intentional connection attempts for anti-fraud detection
// Suppressed in both development and production by default
setupGeoComplyConsoleFilter({ suppressInProduction: true })

// Initialize Meta Pixel for conversion tracking
// Tracks PageView on init, CompleteRegistration and Purchase events are tracked in their respective contexts
initPixel()

// Note: nSure SDK initialization is handled by CoinflowPurchaseProtection component in App.tsx
// This ensures the SDK is loaded on every page for chargeback protection

// Register Service Worker for PWA and Push Notifications
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        if (import.meta.env.DEV) {
          console.log('✅ Service Worker registered:', registration.scope);
        }
      })
      .catch((error) => {
        if (import.meta.env.DEV) {
          console.error('❌ Service Worker registration failed:', error);
        }
      });
  });
}

// Diagnostic logging in development
if (import.meta.env.DEV) {
  console.log('🚀 Customer Frontend starting...');
  console.log('📍 API URL:', import.meta.env.VITE_API_URL || 'http://localhost:3000 (default)');
  
  const rootElement = document.getElementById("root");
  if (!rootElement) {
    console.error('❌ Root element not found! Check index.html');
  } else {
    console.log('✅ Root element found');
  }
}

// Hide the initial loading screen once React mounts
const hideInitialLoader = () => {
  const loader = document.getElementById('initial-loader');
  if (loader) {
    // Fade out animation
    loader.style.transition = 'opacity 0.3s ease-out';
    loader.style.opacity = '0';
    // Remove from DOM after animation
    setTimeout(() => loader.remove(), 300);
  }
};

// Simple error fallback component
const ErrorFallback = () => (
  <div style={{
    padding: '40px',
    textAlign: 'center',
    fontFamily: 'system-ui, sans-serif',
    backgroundColor: 'hsl(220, 20%, 8%)',
    color: 'hsl(210, 40%, 98%)',
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    alignItems: 'center',
  }}>
    <h1>Something went wrong</h1>
    <p>Please refresh the page or contact support if the issue persists.</p>
    <button
      onClick={() => window.location.reload()}
      style={{
        padding: '10px 20px',
        fontSize: '16px',
        cursor: 'pointer',
        marginTop: '20px',
        backgroundColor: 'hsl(45, 93%, 58%)',
        color: 'hsl(220, 20%, 8%)',
        border: 'none',
        borderRadius: '8px',
      }}
    >
      Refresh Page
    </button>
  </div>
);

createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
    <App />
  </Sentry.ErrorBoundary>
);

// Hide loader after React renders
// Use requestAnimationFrame to ensure DOM is ready
requestAnimationFrame(() => {
  requestAnimationFrame(() => {
    hideInitialLoader();
  });
});
